import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, ArrowUpDown, Check, Download, FileText, MoreHorizontal, Pencil, Pin, Plus, Search, Sheet, Trash2, Upload, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/landing/Logo";
import { fmtClock } from "./lib";
import "./condoor.css";

const STATUS = {
  analyzing: ["Analyzing", "b-arch"], review: ["Supplier reviews", "b-confirm"], sent: ["With architect", "b-code"],
  approved: ["Approved", "b-resolved"], failed: ["Needs attention", "b-conflict"],
};

export default function Projects() {
  const { me, signOut } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [showArchived, setShowArchived] = useState(false);
  const [modal, setModal] = useState(null); // "new" | project (edit)
  const [menu, setMenu] = useState(null);
  const [err, setErr] = useState("");

  const load = () => api.get("/condoor/projects").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  const list = useMemo(() => {
    let ps = (data?.projects || []).filter((p) => showArchived || !p.archived);
    if (q.trim()) {
      const t = q.toLowerCase();
      ps = ps.filter((p) => (p.name || "").toLowerCase().includes(t));
    }
    const by = { recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""), name: (a, b) => a.name.localeCompare(b.name),
      status: (a, b) => (a.status || "").localeCompare(b.status || "") }[sort];
    return [...ps].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || by(a, b));
  }, [data, q, sort, showArchived]);

  const patch = async (p, body) => { await api.patch(`/condoor/projects/${p.id}`, body); load(); };
  const remove = async (p) => { await api.delete(`/condoor/projects/${p.id}`); load(); };

  return (
    <div className="cn min-h-screen" data-testid="projects-page">
      <header className="flex h-14 items-center justify-between border-b border-[var(--line)] bg-[var(--bg)] px-6">
        <div className="flex items-center gap-4">
          <Link to="/" aria-label="Condoor home"><Logo className="h-6" /></Link>
          <span className="text-[var(--line2)]">/</span>
          <span className="font-semibold">{data?.supplier || me?.orgs?.[0]?.name || "Projects"}</span>
        </div>
        <div className="flex items-center gap-3 text-[13px] text-[var(--ink2)]">
          {me?.demo && <span className="badge b-met" data-testid="demo-badge">Demo workspace · synthetic data</span>}
          <span>{me?.user?.name}</span>
          <button className="btn btn-ghost btn-sm" onClick={async () => { await signOut(); nav("/"); }}>Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-[1180px] px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="panel-title">Door hardware submittals</p>
            <h1 className="mt-1 text-[32px] font-semibold tracking-[-0.02em]">Projects</h1>
          </div>
          <button className="btn btn-primary h-10 px-5" onClick={() => setModal("new")} data-testid="new-project-button"><Plus size={16} /> New project</button>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <label className="flex h-9 flex-1 items-center gap-2 rounded-full border border-[var(--line2)] bg-white px-3 min-w-[240px]">
            <Search size={15} className="text-[var(--muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects" className="w-full bg-transparent outline-none"
              data-testid="projects-search" />
          </label>
          <label className="flex h-9 items-center gap-2 rounded-full border border-[var(--line2)] bg-white px-3 text-[13px]">
            <ArrowUpDown size={14} className="text-[var(--muted)]" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-transparent outline-none" data-testid="projects-sort">
              <option value="recent">Most recent</option><option value="name">Name</option><option value="status">Status</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-[var(--ink2)]">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived
          </label>
        </div>
        {err && <p className="mt-6 text-[var(--red)]">{err}</p>}
        {data && list.length === 0 && (
          <div className="mt-10 rounded-2xl border border-dashed border-[var(--line2)] px-8 py-16 text-center" data-testid="projects-empty">
            <p className="text-[18px] font-semibold">{q ? "No projects match that search" : "Start your first submittal"}</p>
            <p className="mx-auto mt-2 max-w-md text-[var(--ink2)]">Upload the architect's spec section 08 71 00 and the door and frame schedule. Condoor builds the
              submittal from your catalog and the public codes, and asks you only where the documents disagree.</p>
            {!q && <button className="btn btn-primary mt-6 h-10 px-5" onClick={() => setModal("new")}><Plus size={16} /> New project</button>}
          </div>
        )}
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => {
            const [label, cls] = STATUS[p.status] || [p.status, "b-met"];
            return (
              <div key={p.id} className={`card group relative flex flex-col p-5 transition hover:border-[var(--ink2)] ${p.archived ? "opacity-60" : ""}`}
                data-testid={`project-card-${p.id}`}>
                <Link to={`/p/${p.id}`} className="absolute inset-0 rounded-[10px]" aria-label={`Open ${p.name}`} />
                <div className="flex items-start justify-between gap-3">
                  <span className={`badge ${cls}`}>{label}{p.status === "review" && p.open_decisions ? ` · ${p.open_decisions} open` : ""}</span>
                  <div className="relative z-10 flex items-center gap-1">
                    {p.pinned && <Pin size={14} className="text-[var(--accent)]" />}
                    <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setMenu(menu === p.id ? null : p.id); }} aria-label="Project menu"
                      data-testid={`project-menu-${p.id}`}><MoreHorizontal size={16} /></button>
                    {menu === p.id && (
                      <div className="absolute right-0 top-8 z-20 w-44 rounded-lg border border-[var(--line2)] bg-white p-1 shadow-lg" onClick={(e) => e.stopPropagation()}>
                        {[[Pin, p.pinned ? "Unpin" : "Pin", () => patch(p, { pinned: !p.pinned })],
                          [Pencil, "Edit details", () => setModal(p)],
                          [Archive, p.archived ? "Unarchive" : "Archive", () => patch(p, { archived: !p.archived })],
                          [Trash2, "Delete", () => remove(p)]].map(([I, l, fn]) => (
                          <button key={l} className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] hover:bg-[var(--panel)] ${l === "Delete" ? "text-[var(--red)]" : ""}`}
                            onClick={() => { setMenu(null); fn(); }}><I size={14} /> {l}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <h3 className="mt-4 text-[17px] font-semibold leading-snug">{p.name}</h3>
                <p className="mono mt-1 text-[var(--muted)]">Division 8 · 08 71 00</p>
                <div className="mt-5 grid grid-cols-2 gap-3 border-t border-[var(--line)] pt-4 text-[12.5px]">
                  <div><p className="text-[var(--muted)]">{p.approved_at ? "Approved in" : "Created"}</p>
                    <p className="font-medium">{p.approved_at ? fmtClock((new Date(p.approved_at) - new Date(p.created_at)) / 1000)
                      : new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p></div>
                  {p.summary && <div className="col-span-2 text-[var(--ink2)]">{p.summary.doors} doors · {p.summary.lines} line items</div>}
                </div>
              </div>
            );
          })}
        </div>
      </main>
      {modal === "new" && <NewProject onClose={() => setModal(null)} onCreated={(id) => nav(`/p/${id}`)} />}
      {modal && modal !== "new" && <EditProject p={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

const SLOTS = [
  { role: "spec", title: "Spec section 08 71 00", hint: "Required · PDF or DOCX", accept: ".pdf,.docx", required: true, sample: "spec", icon: FileText },
  { role: "schedule", title: "Door & frame schedule", hint: "Required · PDF or XLSX, with plan references", accept: ".pdf,.xlsx,.csv", required: true,
    sample: "schedule", icon: Sheet },
  { role: "interfaces", title: "Security & electrical interfaces", hint: "Optional · PDF or XLSX", accept: ".pdf,.xlsx,.csv", sample: "interfaces", icon: FileText },
];
const LATER = ["Floor plans and elevations", "Addenda"];

function NewProject({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", invite_email: "" });
  const [files, setFiles] = useState({}); // role → File | {sample, name}
  const [step, setStep] = useState(1);
  const [samples, setSamples] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const ready = files.spec && files.schedule && f.name.trim();

  useEffect(() => { api.get("/condoor/samples").then((r) => setSamples(r.data.samples)).catch(() => {}); }, []);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [busy, onClose]);

  const useSamples = () => {
    const by = Object.fromEntries(samples.map((s) => [s.key, s]));
    setFiles({ spec: { sample: "spec", ...by.spec }, schedule: { sample: "schedule", ...by.schedule }, interfaces: { sample: "interfaces", ...by.interfaces } });
    setF((x) => ({ ...x, name: x.name || "YC Office Fit-out" }));
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    Object.entries(files).forEach(([role, v]) => (v.sample ? fd.append(`${role}_sample`, v.sample) : fd.append(role, v)));
    try {
      const { data } = await api.post("/condoor/projects", fd);
      onCreated(data.id);
    } catch (e) {
      setErr(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="cn cn-overlay fixed inset-0 z-50 grid place-items-center p-4 backdrop-blur-[2px]" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="card fade-up flex max-h-[92vh] w-full max-w-[640px] flex-col overflow-hidden shadow-2xl" role="dialog" aria-label="New project" data-testid="new-project-modal">
        <div className="flex items-center justify-between border-b border-[var(--line)] px-6 py-4">
          <h2 className="text-[18px] font-semibold">New project</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>
        <div className="flex gap-2 px-6 pt-4 text-[12px] font-medium" data-testid="np-steps">
          {["Project", "Contract documents"].map((l, i) => (
            <span key={l} className={`flex items-center gap-1.5 ${step === i + 1 ? "text-[var(--ink)]" : "text-[var(--muted)]"}`}>
              <span className={`grid h-5 w-5 place-items-center rounded-full text-[11px] ${step === i + 1 ? "bg-[var(--ink)] text-white" : "border border-[var(--line2)]"}`}>{i + 1}</span>
              {l}{i === 0 && <span className="mx-1 text-[var(--line2)]">—</span>}
            </span>
          ))}
        </div>
        <div className="scroll flex-1 space-y-5 px-6 py-5">
          {step === 1 ? (
            <>
              <Field label="Project name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="np-name" />
            </>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">Contract documents</p>
                  <button className="btn btn-sm" onClick={useSamples} disabled={!samples.length} data-testid="np-use-samples">Use sample documents</button>
                </div>
                <div className="mt-3 space-y-2">
                  {SLOTS.map((s) => <Slot key={s.role} slot={s} value={files[s.role]} onChange={(v) => setFiles((x) => ({ ...x, [s.role]: v }))} />)}
                  {LATER.map((l) => (
                    <div key={l} className="flex items-center justify-between rounded-lg border border-dashed border-[var(--line)] px-4 py-2.5 text-[13px] text-[var(--muted)]">
                      <span>{l}</span><span className="text-[12px]">After v1</span>
                    </div>
                  ))}
                </div>
                {files.spec?.sample && (
                  <p className="mt-2 text-[12px] text-[var(--ink2)]">Sample set: a 100-page spec for a 52-door office, with the A-601 schedule and SEC-701 sheet generated from it.
                    Synthetic documents only. <a className="underline" href={`${api.defaults.baseURL}/condoor/samples/schedule_pdf`}>A-601 as PDF</a></p>
                )}
              </div>
              <Field label="Invite the architect (optional)" placeholder="name@architect.com" value={f.invite_email}
                onChange={(v) => setF({ ...f, invite_email: v })} testId="np-invite" />
            </>
          )}
          {err && <p className="text-[13px] text-[var(--red)]" data-testid="np-error">{err}</p>}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-[var(--line)] px-6 py-4">
          {step === 2 ? <button className="btn btn-ghost" onClick={() => setStep(1)} disabled={busy} data-testid="np-back">Back</button> : <span />}
          {step === 1 ? (
            <button className="btn btn-primary h-10 px-5" disabled={!f.name.trim()} onClick={() => setStep(2)} data-testid="np-next">Next</button>
          ) : (
            <button className="btn btn-primary h-10 px-5" disabled={!ready || busy} onClick={submit} data-testid="np-create">
              {busy ? "Uploading…" : "Create project and analyze"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Slot({ slot, value, onChange }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const I = slot.icon;
  const pick = (file) => file && onChange(file);
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 transition ${value ? "border-[var(--cat)] bg-[var(--cat-bg)]" : over ? "border-[var(--accent)] bg-[var(--accent-bg)]" : "border-[var(--line2)] bg-white"}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]); }} data-testid={`np-slot-${slot.role}`}>
      <I size={18} className={value ? "text-[var(--cat)]" : "text-[var(--muted)]"} />
      <div className="min-w-0 flex-1">
        <p className="text-[13.5px] font-medium">{slot.title}</p>
        <p className="truncate text-[12px] text-[var(--ink2)]">{value ? value.name : slot.hint}</p>
      </div>
      {value ? (
        <div className="flex items-center gap-1">
          {value.sample && <a className="icon-btn" href={`${api.defaults.baseURL}/condoor/samples/${value.sample}`} aria-label="Download sample"><Download size={14} /></a>}
          <Check size={16} className="text-[var(--cat)]" />
          <button className="icon-btn" onClick={() => onChange(undefined)} aria-label={`Remove ${slot.title}`}><X size={14} /></button>
        </div>
      ) : (
        <button className="btn btn-sm" onClick={() => input.current?.click()}><Upload size={13} /> Upload</button>
      )}
      <input ref={input} type="file" accept={slot.accept} hidden onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}

function Field({ label, value, onChange, placeholder, hint, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[12.5px] font-medium text-[var(--ink2)]">{label}</span>
      <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-1 h-10 w-full rounded-lg border border-[var(--line2)] bg-white px-3 outline-none focus:border-[var(--accent)]" />
      {hint && <span className="mt-1 block text-[12px] text-[var(--muted)]">{hint}</span>}
    </label>
  );
}

function EditProject({ p, onClose, onSaved }) {
  const [f, setF] = useState({ name: p.name });
  const save = async () => { await api.patch(`/condoor/projects/${p.id}`, f); onSaved(); };
  return (
    <div className="cn cn-overlay fixed inset-0 z-50 grid place-items-center p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card fade-up w-full max-w-[460px] p-6 shadow-2xl" role="dialog" aria-label="Edit details">
        <h2 className="text-[18px] font-semibold">Edit details</h2>
        <div className="mt-4 space-y-3">
          <Field label="Project name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={!f.name.trim()}>Save</button>
        </div>
      </div>
    </div>
  );
}
